/**
 * TrackingManager — orchestrates the tracker subsystem.
 *
 * Lifecycle:
 *   1. `bootGame()` constructs the manager (no trackers loaded yet).
 *   2. If the manifest declares `requirements.tracking`, `enable([...])`
 *      lazy-loads MediaPipe + the requested tracker(s) in parallel.
 *   3. Once the camera is live, `startAll(videoElement)` hands the same
 *      `<video>` element to every tracker so they share one frame source.
 *   4. `stopAll()` halts inference. `destroyAll()` releases everything.
 *
 * The manager exposes the trackers as `this.pose` / `this.face` /
 * `this.hands` (or `null` if a kind wasn't enabled), so game code can do:
 *
 *   if (this.sdk.tracking.pose) {
 *     const wrist = this.sdk.tracking.pose.getLandmark('rightWrist');
 *     ...
 *   }
 *
 * The factory functions in `_factories` are overridable to support
 * tests with stub trackers.
 */
import { PoseTracker } from './PoseTracker.js';
import { FaceTracker } from './FaceTracker.js';
import { HandTracker } from './HandTracker.js';

export class TrackingManager {
  /**
   * @param {object} [opts]
   * @param {object} [opts.factories] - override `pose`/`face`/`hands` factories (for tests)
   */
  constructor(opts = {}) {
    /** @type {PoseTracker|null} */
    this.pose = null;
    /** @type {FaceTracker|null} */
    this.face = null;
    /** @type {HandTracker|null} */
    this.hands = null;

    this._enabled = new Set();
    this._factories = Object.assign({
      pose:  (o) => new PoseTracker(o),
      face:  (o) => new FaceTracker(o),
      hands: (o) => new HandTracker(o)
    }, opts.factories || {});
  }

  /**
   * Lazy-instantiate and load the requested tracker kinds in parallel.
   * @param {Array<'pose'|'face'|'hands'>} kinds
   * @param {object} [perKindOpts]
   */
  async enable(kinds, perKindOpts = {}) {
    if (!Array.isArray(kinds)) return;
    const promises = [];
    for (const kind of kinds) {
      if (this._enabled.has(kind)) continue;
      const factory = this._factories[kind];
      if (!factory) continue;
      const tracker = factory(perKindOpts[kind] || {});
      this[kind] = tracker;
      this._enabled.add(kind);
      promises.push(tracker.load());
    }
    await Promise.all(promises);
  }

  /** Begin per-frame inference for every enabled tracker. */
  async startAll(videoElement) {
    const promises = [];
    if (this.pose)  promises.push(this.pose.start(videoElement));
    if (this.face)  promises.push(this.face.start(videoElement));
    if (this.hands) promises.push(this.hands.start(videoElement));
    await Promise.all(promises);
  }

  /** Stop inference but keep the trackers loaded. */
  stopAll() {
    if (this.pose)  try { this.pose.stop(); }  catch (_) { /* ignore */ }
    if (this.face)  try { this.face.stop(); }  catch (_) { /* ignore */ }
    if (this.hands) try { this.hands.stop(); } catch (_) { /* ignore */ }
  }

  /** Stop and dispose every tracker. */
  destroyAll() {
    if (this.pose)  this.pose.destroy();
    if (this.face)  this.face.destroy();
    if (this.hands) this.hands.destroy();
    this.pose = null;
    this.face = null;
    this.hands = null;
    this._enabled.clear();
  }

  /** Names of currently-enabled tracker kinds. */
  getEnabled() {
    return Array.from(this._enabled);
  }
}
